import type { ReactNode } from "react";
import { useTenant } from "@/tenant/TenantProvider";
import { AppFooter } from "./AppFooter";
import { ScrollablePage } from "@/ui";
import { LanguageSwitch } from "@/i18n/LanguageSwitch";

/**
 * Split-screen sign-in shell.
 *
 * Design notes, because the first version had real problems:
 *
 *  • The panel is wider than the form column (1.15fr / 1fr), not a 50/50 split.
 *    An even split left the card marooned in the middle of a huge empty half.
 *  • Panel content is ONE optically-centred stack — logo, headline, blurb,
 *    proof points — instead of three blocks pinned to top, middle and bottom
 *    with voids between them.
 *  • The background motif is a QR module grid and a finder pattern rather than
 *    blurred colour blobs. It belongs to this product specifically, and it
 *    reads as texture rather than decoration.
 *  • Proof points use small brand-coloured squares — QR modules — not tick
 *    circles.
 *
 * Every colour comes from a branding token, including the panel itself, so
 * this screen rebrands with the rest of the app.
 */

export interface AuthLayoutProps {
  /** Headline; `accent` renders in the brand colour. */
  headline: string;
  accent: string;
  blurb: string;
  points: readonly string[];
  /**
   * Show the Hindi/English switch. Only the karigar sign-in has copy in both
   * languages, and nobody is signed in yet to pick one from a profile.
   */
  languageSwitch?: boolean;
  children: ReactNode;
}

export function AuthLayout({ headline, accent, blurb, points, languageSwitch = false, children }: AuthLayoutProps) {
  const { payload } = useTenant();
  const brand = payload.branding;
  const year = new Date().getFullYear();

  /*
   * D-15 exempts the public screens: the workspace never scrolls, but a
   * sign-in form is a document and has to survive a short phone viewport.
   * Without this marker `body { overflow: hidden }` would clip the submit
   * button out of reach with no way to get to it.
   */
  return (
    <ScrollablePage>
    <div className="grid min-h-dvh lg:grid-cols-[1.15fr_1fr]">
      <aside className="relative hidden overflow-hidden bg-panel px-14 py-12 text-panel-text lg:flex lg:flex-col xl:px-20">
        <PanelTexture />

        <div className="relative flex flex-1 flex-col justify-center">
          <div className="max-w-[27rem]">
            <Wordmark brand={brand} className="mb-14 h-7" hasOwnLogo={payload.hasOwnLogo} onPanel />

            <h2 className="font-title text-[2.75rem] leading-[1.08] font-semibold tracking-[-0.03em]">
              {headline}
              <br />
              <span className="text-brand-on-panel">{accent}</span>
            </h2>

            <p className="mt-6 text-[17px] leading-relaxed text-panel-text/70">{blurb}</p>

            <ul className="mt-10 space-y-3">
              {points.map((point) => (
                <li
                  className="flex items-center gap-3.5 rounded-md border border-panel-text/10 bg-panel-text/[0.04] px-4 py-3 text-sm text-panel-text/90"
                  key={point}
                >
                  <span
                    aria-hidden="true"
                    className="grid size-2.5 shrink-0 grid-cols-2 gap-px"
                  >
                    <span className="rounded-[1px] bg-brand-on-panel" />
                    <span className="rounded-[1px] bg-brand-on-panel" />
                    <span className="rounded-[1px] bg-brand-on-panel" />
                    <span />
                  </span>
                  {point}
                </li>
              ))}
            </ul>
          </div>
        </div>

        {/* On the panel the footer inherits the panel's own text colour, so it
            gets its own copy rather than reusing <AppFooter>, which is tuned
            for the light surface. Same content, same sources. */}
        <p className="relative text-xs text-panel-text/45">
          © {year} {payload.orgName ?? brand.appName}
          {payload.platformAppName && payload.platformAppName !== (payload.orgName ?? brand.appName)
            ? ` · Powered by ${payload.platformAppName}`
            : ""}
          {brand.supportPhone ? (
            <>
              {" · Support: "}
              <a
                className="text-panel-text/75 transition-colors hover:text-panel-text"
                href={`tel:${brand.supportPhone.replace(/\s/g, "")}`}
              >
                {brand.supportPhone}
              </a>
            </>
          ) : null}
          {brand.supportEmail ? (
            <>
              {" · "}
              <a
                className="text-panel-text/75 transition-colors hover:text-panel-text"
                href={`mailto:${brand.supportEmail}`}
              >
                {brand.supportEmail}
              </a>
            </>
          ) : null}
        </p>
      </aside>

      <main className="gq-canvas relative flex flex-col items-center gap-6 overflow-hidden pb-12 lg:justify-center lg:px-5 lg:py-12">
        {/* Phones never see the panel, so they get the brand as a band
            across the top instead — the card rides up over its edge, as it
            does in the karigar app. */}
        <div className="relative w-full overflow-hidden bg-panel px-6 pt-[max(1.5rem,env(safe-area-inset-top))] pb-20 text-panel-text lg:hidden">
          <div aria-hidden="true" className="gq-modules pointer-events-none absolute inset-0 opacity-[0.07]" />
          <div
            aria-hidden="true"
            className="pointer-events-none absolute -top-24 -right-16 size-64 rounded-full bg-brand/25 blur-[70px]"
          />
          <div className="relative mx-auto max-w-[25rem]">
            <div className="flex items-center justify-between gap-3">
              <Wordmark brand={brand} className="h-7" hasOwnLogo={payload.hasOwnLogo} onPanel />
              {languageSwitch ? <LanguageSwitch fallback="hi" tone="panel" /> : null}
            </div>
            <p className="mt-4 font-title text-2xl leading-tight font-semibold tracking-[-0.02em]">
              {headline} <span className="text-brand-on-panel">{accent}</span>
            </p>
          </div>
        </div>
        <div
          aria-hidden="true"
          className="gq-modules pointer-events-none absolute inset-0 hidden text-ink opacity-[0.045] [mask-image:radial-gradient(60%_50%_at_50%_45%,black,transparent)] lg:block"
        />
        {languageSwitch ? (
          <div className="absolute top-6 right-6 z-10 hidden lg:block">
            <LanguageSwitch fallback="hi" showIcon />
          </div>
        ) : null}
        <div className="relative -mt-14 w-full max-w-[25rem] animate-rise px-4 lg:mt-0 lg:px-0">{children}</div>
        <AppFooter className="relative px-5 lg:hidden" />
      </main>
    </div>
    </ScrollablePage>
  );
}

/**
 * A QR module grid, plus one finder pattern bled off the bottom-right corner.
 * Pure CSS — no asset, no library — and it says "this is a QR product" without
 * a single word.
 */
function PanelTexture() {
  return (
    <>
      <div
        aria-hidden="true"
        className="pointer-events-none absolute inset-0 opacity-[0.055]"
        style={{
          backgroundImage:
            "radial-gradient(currentColor 1px, transparent 1px), radial-gradient(currentColor 1px, transparent 1px)",
          backgroundSize: "22px 22px, 22px 22px",
          backgroundPosition: "0 0, 11px 11px",
        }}
      />
      {/* Three finder patterns — the corners of one enormous QR code, bled
          off the panel's edge. */}
      <div
        aria-hidden="true"
        className="pointer-events-none absolute -right-36 -bottom-36 grid size-[26rem] rotate-[8deg] grid-cols-[1fr_1fr] grid-rows-[1fr_1fr] gap-16 opacity-[0.08]"
      >
        <Finder />
        <Finder />
        <Finder />
      </div>
      {/* One soft lift so the flat panel is not a dead rectangle. */}
      <div
        aria-hidden="true"
        className="pointer-events-none absolute -top-40 -left-24 size-[28rem] rounded-full bg-brand/20 blur-[100px]"
      />
    </>
  );
}

function Finder() {
  return (
    <div className="size-full rounded-[22px] border-[18px] border-current p-7">
      <div className="size-full rounded-[8px] bg-current" />
    </div>
  );
}

/** The card every sign-in form sits in. */
export function AuthCard({
  title,
  subtitle,
  children,
  footer,
}: {
  title: string;
  subtitle?: string;
  children: ReactNode;
  footer?: ReactNode;
}) {
  // The wordmark is not repeated in here: on a phone the brand band above
  // carries it, on a desktop the panel does.
  return (
    <div className="relative overflow-hidden rounded-lg bg-raised px-7 pt-8 pb-7 shadow-pop ring-1 ring-black/[0.06] sm:px-8">
      <span aria-hidden="true" className="absolute inset-x-0 top-0 h-1 bg-brand" />
      <h1 className="font-title text-2xl leading-tight font-semibold tracking-[-0.02em] text-ink">
        {title}
      </h1>
      {subtitle ? <p className="mt-1.5 text-sm text-muted">{subtitle}</p> : null}

      <div className="mt-7">{children}</div>

      {footer ? (
        <div className="mt-6 border-t border-rule pt-5 text-center text-sm text-muted">{footer}</div>
      ) : null}
    </div>
  );
}

function Wordmark({
  brand,
  className = "",
  onPanel = false,
  hasOwnLogo = true,
}: {
  brand: Record<string, string>;
  className?: string;
  onPanel?: boolean;
  /** False for an org still inheriting the platform mark — show its name instead. */
  hasOwnLogo?: boolean;
}) {
  const inherited = onPanel ? brand.logoDark || brand.logo : brand.logo;
  const src = hasOwnLogo ? inherited : null;

  if (src) {
    return (
      <img alt={brand.appName ?? ""} className={`w-auto object-contain ${className}`} src={src} />
    );
  }
  return (
    <span
      className={`flex items-center font-title text-lg font-semibold tracking-[-0.01em] ${
        onPanel ? "text-panel-text" : "text-brand-ink"
      } ${className}`}
    >
      {brand.appName}
    </span>
  );
}
